// src/pages/FundPlanner/lib/fetchMarketRates.js
import { supabase } from "services/supabaseClient";
import dayjs from "lib/dayjs";
import { HYPOTHETICAL_CURVE_UNIVERSES } from "./usTreasuryCalculator";

export async function fetchMarketRates({
  securityType = "slgs",
  asOfDate,
  maxMaturityDate,
}) {
  if (!asOfDate) return [];

  const selectedDate = dayjs(asOfDate).format("YYYY-MM-DD");

  // Hypothetical curves use fixed, dated observations. Fetch every supported
  // node so the calculator can validate exact interpolation bounds.
  const hypotheticalConfig = HYPOTHETICAL_CURVE_UNIVERSES[securityType];
  if (hypotheticalConfig) {
    const { data, error } = await supabase
      .from("market_data")
      .select("date, security_id, ticker, tenor, value, units, source, name")
      .eq("date", selectedDate)
      .in("security_id", hypotheticalConfig.securityIds);

    if (error) {
      console.error(`fetchMarketRates(${securityType}) error:`, error.message);
      return [];
    }

    return (data || []).map((row) => ({
      ...row,
      code: row.security_id,
      ratePct:
        row.value == null || String(row.value).trim() === ""
          ? null
          : Number(String(row.value).replace("%", "")),
      asOf: selectedDate,
    }));
  }

  // ── 1️⃣ Stable universe filter ──
  let idFilter;
  switch (securityType) {
    case "slgs":
    default:
      idFilter = "slgs-time%";
      break;
  }

  // ── 2️⃣ Query *only* that market date ──
  const { data, error } = await supabase
    .from("market_data")
    .select("date, security_id, ticker, tenor, value, units, source, name")
    .eq("date", selectedDate)                    // <── exact market date only
    .ilike("security_id", idFilter)
    .order("tenor", { ascending: true });

  if (error) {
    console.error(`❌ fetchMarketRates(${securityType}) error:`, error.message);
    return [];
  }
  if (!data?.length) {
    console.warn(`⚠️ No ${securityType} data found for ${selectedDate}`);
    return [];
  }

  // ── 3️⃣ Parse tenor → months ──
  const parseTenorToMonths = (rawTenor) => {
    if (!rawTenor) return 0;
    const t = String(rawTenor).trim().toLowerCase().replace(/\s+/g, "");
    const m = t.match(/^(?:(\d+)y)?(?:(\d+)m)?$/i);
    if (m) {
      const years = parseInt(m[1] || "0", 10);
      const months = parseInt(m[2] || "0", 10);
      return years * 12 + months;
    }
    const yearPart = t.match(/(\d+)\s*(y|yr|yrs|year|years)/i);
    const monthPart = t.match(/(\d+)\s*(m|mo|mos|mon|mons|month|months)/i);
    const y = yearPart ? parseInt(yearPart[1], 10) : 0;
    const mo = monthPart ? parseInt(monthPart[1], 10) : 0;
    return y * 12 + mo;
  };

  // ── 4️⃣ Optional max maturity horizon ──
  let maxMonths = Infinity;
  if (maxMaturityDate) {
    const months = dayjs(maxMaturityDate).diff(dayjs(selectedDate), "month", true);
    maxMonths = Math.max(months, 0);
  }

  // ── 5️⃣ Clean and sort ──
  const bucket = new Map();
  for (const r of data) {
    const months = parseTenorToMonths(r.tenor);
    if (months <= 0 || months > maxMonths) continue;
    const ratePct = parseFloat(String(r.value).replace("%", ""));
    if (!Number.isFinite(ratePct)) continue;
    if (!bucket.has(months)) {
      bucket.set(months, {
        code: r.security_id,
        tenor: r.tenor,
        months,
        ratePct,
        asOf: selectedDate,
        source: r.source,
        units: r.units,
      });
    }
  }

  const result = Array.from(bucket.values()).sort((a, b) => a.months - b.months);

  return result;
}
